import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RotateCcw, Check, Move } from "lucide-react";
import { toast } from "sonner";
import { getWallpaperPosition, setWallpaperPosition } from "@/lib/wallpaper";

/**
 * Full-screen editor that lets the user drag a custom wallpaper around inside
 * a viewport-shaped frame to pick the exact crop that shows through.
 *
 * The math: for `object-fit: cover` the image is scaled up until BOTH
 * dimensions fill the frame; whichever dimension is bigger becomes the
 * scrollable overflow. `object-position: X% Y%` slides the image within that
 * overflow — 0% shows the top/left edge, 100% shows the bottom/right.
 * Dragging the image RIGHT by `dx` pixels should reveal MORE of the left of
 * the image → decrease X%, hence the negative sign in the delta below.
 */
export default function WallpaperRepositionDialog({ open, onOpenChange, wallpaper }) {
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [dragging, setDragging] = useState(false);
  const startRef = useRef(null);
  const imgRef = useRef(null);
  const frameRef = useRef(null);
  const [aspect, setAspect] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth / window.innerHeight : 16 / 9
  );

  // Load saved position when the dialog opens and lock aspect to the caller's
  // real viewport so the preview mirrors what the user will actually see.
  useEffect(() => {
    if (!open || !wallpaper?.id) return;
    setPosition(getWallpaperPosition(wallpaper.id));
    setAspect(window.innerWidth / window.innerHeight);
  }, [open, wallpaper?.id]);

  // Global pointer listeners while dragging — using window listeners means
  // the drag continues even when the pointer briefly leaves the frame.
  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      if (!startRef.current) return;
      const s = startRef.current;
      const p = e.touches?.[0] || e;
      const dx = p.clientX - s.px;
      const dy = p.clientY - s.py;
      const nextX = s.overflowX > 0 ? clamp01(s.x - (dx / s.overflowX) * 100) : 50;
      const nextY = s.overflowY > 0 ? clamp01(s.y - (dy / s.overflowY) * 100) : 50;
      setPosition({ x: nextX, y: nextY });
    };
    const end = () => { setDragging(false); startRef.current = null; };
    const opts = { passive: false };
    window.addEventListener("pointermove", move, opts);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    window.addEventListener("touchmove", move, opts);
    window.addEventListener("touchend", end);
    return () => {
      window.removeEventListener("pointermove", move, opts);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      window.removeEventListener("touchmove", move, opts);
      window.removeEventListener("touchend", end);
    };
  }, [dragging]);

  const beginDrag = (e) => {
    const img = imgRef.current, frame = frameRef.current;
    if (!img || !frame || !img.naturalWidth) return;
    const frameW = frame.clientWidth, frameH = frame.clientHeight;
    const scale = Math.max(frameW / img.naturalWidth, frameH / img.naturalHeight);
    const overflowX = Math.max(0, img.naturalWidth * scale - frameW);
    const overflowY = Math.max(0, img.naturalHeight * scale - frameH);
    if (overflowX < 1 && overflowY < 1) {
      toast.message("Nothing to reposition — this photo already fits the screen.");
      return;
    }
    const p = e.touches?.[0] || e;
    startRef.current = {
      px: p.clientX, py: p.clientY,
      x: position.x, y: position.y,
      overflowX, overflowY,
    };
    setDragging(true);
  };

  const reset = () => setPosition({ x: 50, y: 50 });

  const save = () => {
    if (!wallpaper?.id) return;
    setWallpaperPosition(wallpaper.id, position);
    toast.success("Position saved");
    onOpenChange(false);
  };

  if (!wallpaper?.url) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="wallpaper-reposition-dialog"
        className="bg-[#0A0A0A] border-[#27272A] text-[#F3F4F6] max-w-3xl w-[92vw] p-4 sm:p-6"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Move className="h-4 w-4 text-[#3B82F6]" /> Reposition wallpaper
          </DialogTitle>
          <DialogDescription className="text-[#9CA3AF] text-xs">
            Drag the photo to pick which part shows through your notes. The frame matches your screen shape.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={frameRef}
          data-testid="wallpaper-reposition-frame"
          onPointerDown={beginDrag}
          onTouchStart={beginDrag}
          style={{ aspectRatio: aspect, touchAction: "none" }}
          className={`relative w-full max-h-[65vh] mx-auto rounded-xl overflow-hidden border border-[#27272A] bg-[#0A0A0A] ${
            dragging ? "cursor-grabbing" : "cursor-grab"
          } select-none`}
        >
          <img
            ref={imgRef}
            src={wallpaper.url}
            alt=""
            draggable={false}
            style={{
              objectFit: "cover",
              objectPosition: `${position.x}% ${position.y}%`,
            }}
            className="absolute inset-0 h-full w-full pointer-events-none"
          />
          {/* Subtle center crosshair to give visual feedback while dragging */}
          <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/10" />
          <div
            data-testid="wallpaper-reposition-hint"
            className={`absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/60 text-[11px] text-white/90 flex items-center gap-1.5 transition-opacity ${
              dragging ? "opacity-0" : "opacity-100"
            }`}
          >
            <Move className="h-3 w-3" /> Drag to reposition
          </div>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full bg-black/60 text-[10px] tabular-nums text-white/70">
            x {Math.round(position.x)} · y {Math.round(position.y)}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 pt-4">
          <Button
            data-testid="wallpaper-reposition-reset"
            onClick={reset}
            variant="ghost"
            className="rounded-full text-[#9CA3AF] hover:bg-[#1E1E1E]"
          >
            <RotateCcw className="h-4 w-4 mr-2" /> Reset to center
          </Button>
          <div className="flex items-center gap-2">
            <Button
              data-testid="wallpaper-reposition-cancel"
              onClick={() => onOpenChange(false)}
              variant="ghost"
              className="rounded-full text-[#9CA3AF] hover:bg-[#1E1E1E]"
            >
              Cancel
            </Button>
            <Button
              data-testid="wallpaper-reposition-save"
              onClick={save}
              className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            >
              <Check className="h-4 w-4 mr-2" /> Save position
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function clamp01(v) { return Math.max(0, Math.min(100, v)); }
